import React, { useRef, useEffect, useState, useCallback } from 'react';
import * as d3 from 'd3';

const AXES = [
  { key: 'pest365', label: 'Pest365' },
  { key: 'pest365Plus', label: 'Pest365+' },
  { key: 'oneTime', label: 'One-Time' },
  { key: 'initialServiceVisit', label: 'Init. Visit' },
  { key: 'mosquitoTickControl', label: 'Mosquito/Tick' },
];

const NUM_AXES = AXES.length;
const ANGLE_SLICE = (2 * Math.PI) / NUM_AXES;
const RING_LEVELS = [0.25, 0.5, 0.75, 1.0];

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '4px',
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    marginBottom: '16px',
  },
  wrapper: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '16px',
  },
  chartContainer: {
    flex: 1,
    minWidth: '400px',
  },
  legendPanel: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    minWidth: '100px',
    maxHeight: '500px',
    overflowY: 'auto',
    paddingRight: '4px',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 500,
    userSelect: 'none',
    padding: '3px 6px',
    borderRadius: '4px',
    transition: 'background 0.15s',
  },
  legendSwatch: {
    width: '14px',
    height: '14px',
    borderRadius: '3px',
    flexShrink: 0,
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '10px 14px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    maxWidth: '220px',
  },
};

function getStateColor(index) {
  const palette = d3.schemeTableau10;
  return palette[index % palette.length];
}

function computeTotalPrice(d) {
  let total = 0;
  AXES.forEach((axis) => {
    const v = d.raw ? d.raw[axis.key] : null;
    if (v != null) total += v;
  });
  return total;
}

export default function StateRadarChart({ data }) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);
  const [visibleStates, setVisibleStates] = useState(new Set());
  const [initialized, setInitialized] = useState(false);

  // Determine initial visible set when data changes
  useEffect(() => {
    if (!data || data.length === 0) {
      setVisibleStates(new Set());
      setInitialized(false);
      return;
    }

    if (!initialized) {
      if (data.length <= 6) {
        setVisibleStates(new Set(data.map((d) => d.state)));
      } else {
        const sorted = [...data].sort((a, b) => computeTotalPrice(b) - computeTotalPrice(a));
        const top6 = sorted.slice(0, 6).map((d) => d.state);
        setVisibleStates(new Set(top6));
      }
      setInitialized(true);
    }
  }, [data, initialized]);

  const toggleState = useCallback((state) => {
    setVisibleStates((prev) => {
      const next = new Set(prev);
      if (next.has(state)) {
        next.delete(state);
      } else {
        next.add(state);
      }
      return next;
    });
  }, []);

  // Build a color map keyed by state
  const colorMap = {};
  if (data) {
    data.forEach((d, i) => {
      colorMap[d.state] = getStateColor(i);
    });
  }

  useEffect(() => {
    if (!data || data.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const containerWidth = containerRef.current
      ? containerRef.current.clientWidth
      : 500;
    const size = Math.max(400, Math.min(containerWidth, 500));
    const cx = size / 2;
    const cy = size / 2;
    const radius = (Math.min(size, size) / 2) * 0.8;

    svg.attr('width', size).attr('height', size);

    const g = svg.append('g').attr('transform', `translate(${cx}, ${cy})`);

    // Draw concentric rings
    RING_LEVELS.forEach((level) => {
      const r = radius * level;
      g.append('circle')
        .attr('cx', 0)
        .attr('cy', 0)
        .attr('r', r)
        .attr('fill', 'none')
        .attr('stroke', '#e5e7eb')
        .attr('stroke-dasharray', '4,4')
        .attr('stroke-width', 1);

      // Ring level label
      g.append('text')
        .attr('x', 4)
        .attr('y', -r + 2)
        .attr('font-size', '9px')
        .attr('fill', '#9ca3af')
        .attr('alignment-baseline', 'after-edge')
        .text(`${Math.round(level * 100)}%`);
    });

    // Draw axes
    AXES.forEach((axis, i) => {
      const angle = ANGLE_SLICE * i - Math.PI / 2;
      const x2 = radius * Math.cos(angle);
      const y2 = radius * Math.sin(angle);

      // Axis line
      g.append('line')
        .attr('x1', 0)
        .attr('y1', 0)
        .attr('x2', x2)
        .attr('y2', y2)
        .attr('stroke', '#e5e7eb')
        .attr('stroke-width', 1);

      // Axis label
      const labelOffset = 18;
      const lx = (radius + labelOffset) * Math.cos(angle);
      const ly = (radius + labelOffset) * Math.sin(angle);

      let textAnchor = 'middle';
      if (Math.abs(Math.cos(angle)) > 0.1) {
        textAnchor = Math.cos(angle) > 0 ? 'start' : 'end';
      }

      g.append('text')
        .attr('x', lx)
        .attr('y', ly)
        .attr('text-anchor', textAnchor)
        .attr('dominant-baseline', 'central')
        .attr('font-size', '11px')
        .attr('font-weight', 500)
        .attr('fill', '#4b5563')
        .text(axis.label);
    });

    // Draw state polygons
    const visibleData = data.filter((d) => visibleStates.has(d.state));

    visibleData.forEach((stateData) => {
      const color = colorMap[stateData.state];
      const points = AXES.map((axis, i) => {
        const val = stateData[axis.key] != null ? stateData[axis.key] : 0;
        const angle = ANGLE_SLICE * i - Math.PI / 2;
        const r = radius * val;
        return [r * Math.cos(angle), r * Math.sin(angle)];
      });

      const lineGen = d3.line().curve(d3.curveLinearClosed);

      // Filled polygon
      g.append('path')
        .datum(points)
        .attr('d', lineGen)
        .attr('fill', color)
        .attr('fill-opacity', 0.15)
        .attr('stroke', color)
        .attr('stroke-width', 2)
        .attr('stroke-opacity', 1)
        .attr('data-state', stateData.state)
        .style('cursor', 'pointer')
        .on('mouseenter', function () {
          d3.select(this).attr('fill-opacity', 0.35);
        })
        .on('mousemove', (event) => {
          // Determine closest axis
          const rect = svgRef.current.getBoundingClientRect();
          const mx = event.clientX - rect.left - cx;
          const my = event.clientY - rect.top - cy;
          const mouseAngle = Math.atan2(my, mx);

          let closestIdx = 0;
          let minDiff = Infinity;
          AXES.forEach((_, i) => {
            const axAngle = ANGLE_SLICE * i - Math.PI / 2;
            let diff = Math.abs(axAngle - mouseAngle);
            if (diff > Math.PI) diff = 2 * Math.PI - diff;
            if (diff < minDiff) {
              minDiff = diff;
              closestIdx = i;
            }
          });

          setTooltip({
            x: event.clientX + 14,
            y: event.clientY - 14,
            state: stateData.state,
            raw: stateData.raw || {},
            closestAxis: AXES[closestIdx].key,
          });
        })
        .on('mouseleave', function () {
          d3.select(this).attr('fill-opacity', 0.15);
          setTooltip(null);
        });

      // Dots at vertices
      points.forEach((pt) => {
        g.append('circle')
          .attr('cx', pt[0])
          .attr('cy', pt[1])
          .attr('r', 3)
          .attr('fill', color)
          .attr('stroke', '#fff')
          .attr('stroke-width', 1)
          .style('pointer-events', 'none');
      });
    });

    return () => {
      svg.selectAll('*').remove();
    };
  }, [data, visibleStates, colorMap]);

  // Empty state
  if (!data || data.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.title}>State Pricing Profile</div>
        <div style={styles.subtitle}>Normalized comparison across all price dimensions</div>
        <div style={{ color: '#6b7280', textAlign: 'center', padding: '40px 0' }}>
          No state comparison data available
        </div>
      </div>
    );
  }

  const formatDollar = (v) => {
    if (v == null) return 'N/A';
    return `$${v.toFixed(2)}`;
  };

  return (
    <div style={styles.card}>
      <div style={styles.title}>State Pricing Profile</div>
      <div style={styles.subtitle}>Normalized comparison across all price dimensions</div>
      <div style={styles.wrapper}>
        <div style={styles.chartContainer} ref={containerRef}>
          <svg ref={svgRef} />
        </div>
        <div style={styles.legendPanel}>
          {data.map((d) => {
            const visible = visibleStates.has(d.state);
            const color = colorMap[d.state];
            return (
              <div
                key={d.state}
                style={{
                  ...styles.legendItem,
                  background: visible ? 'transparent' : '#f9fafb',
                }}
                onClick={() => toggleState(d.state)}
              >
                <span
                  style={{
                    ...styles.legendSwatch,
                    background: visible ? color : '#d1d5db',
                  }}
                />
                <span
                  style={{
                    color: visible ? '#374151' : '#9ca3af',
                    textDecoration: visible ? 'none' : 'line-through',
                  }}
                >
                  {d.state}
                </span>
              </div>
            );
          })}
        </div>
      </div>
      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '6px', fontSize: '13px' }}>
            {tooltip.state}
          </div>
          {AXES.map((axis) => {
            const isClosest = axis.key === tooltip.closestAxis;
            const rawVal = tooltip.raw[axis.key];
            return (
              <div
                key={axis.key}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: '12px',
                  marginBottom: '2px',
                  background: isClosest ? '#eef2ff' : 'transparent',
                  borderRadius: '3px',
                  padding: '1px 4px',
                }}
              >
                <span style={{ color: isClosest ? '#4338ca' : '#6b7280', fontWeight: isClosest ? 600 : 400 }}>
                  {axis.label}
                </span>
                <span style={{ fontWeight: 600, color: isClosest ? '#4338ca' : '#1f2937' }}>
                  {formatDollar(rawVal)}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
